import { RefObject, useCallback, useEffect } from 'react';
import { create } from 'zustand';

import { useBizyFlightStore } from '../../../../../bizy';

/**
 * Drives the Bizy bee flying to the right of a comment box
 * when the user engages it, and home to the nav when they leave. Pure
 * interaction prototype — no suggestions yet.
 *
 * Single-bee arbitration: only one composer "owns" the flying bee at a time.
 * Focusing a different comment box hands ownership over, and the flight store
 * chains the bee box-to-box rather than routing home first.
 */

/** Retry budget for the fly-in: generous enough to outlast a scroll-into-view. */
const MAX_FLIGHT_FRAMES = 120;
export const COMMENT_ZONE_ATTR = 'data-bizy-comment-zone';
/**
 * How long the landing anchor must hold still before a fresh entrance flies.
 * The feed scroll-into-view fires ~100ms after a box opens, so a couple of
 * frames isn't enough — the bee would fly to the pre-scroll spot and snap.
 * Requiring the anchor to hold still for a window longer than that delay makes
 * it wait until the scroll has actually finished.
 */
const TARGET_STABLE_MS = 150;

/**
 * Grace period before the bee actually flies home. Switching to another comment
 * box tears down the current composer and/or clicks outside it before the new
 * box has mounted and claimed the bee — so an immediate home flight always makes
 * the bee bounce off the nav. Deferring lets the new box claim first (cancelling
 * the pending home) so the bee chains box-to-box. Long enough to cover a
 * composer mount + autofocus, short enough that a genuine "leave" still feels
 * responsive.
 */
const FLY_HOME_GRACE_MS = 320;

// Module-level (only the single owner ever schedules a home), so a claim from a
// different composer instance can cancel a pending home.
let scheduledHomeTimer: ReturnType<typeof setTimeout> | null = null;
const cancelScheduledHome = () => {
	if (scheduledHomeTimer !== null) {
		clearTimeout(scheduledHomeTimer);
		scheduledHomeTimer = null;
	}
};

interface CommentFlightOwnerStore {
	activeBonusId: string | null;
	setActive: (bonusId: string | null) => void;
}

/** Module-level so every mounted composer shares one source of truth for who owns the bee. */
export const useCommentFlightOwner = create<CommentFlightOwnerStore>((set) => ({
	activeBonusId: null,
	setActive: (bonusId) => set({ activeBonusId: bonusId }),
}));

export interface UseBizyCommentBoxFlightParams {
	bonusId: string;
	/** Flag + desktop + motion gate. When false, the hook is inert. */
	enabled: boolean;
	/** The white composer card — used for scroll-away detection and Escape scoping. */
	cardRef: RefObject<HTMLElement | null>;
	/** Invisible marker at the bee's landing spot; registered as the flight destination while owned. */
	anchorRef: RefObject<HTMLElement | null>;
}

export interface UseBizyCommentBoxFlightResult {
	/** True when this composer currently owns the bee. */
	isOwner: boolean;
	/** True once the bee has finished flying and is parked on this box (not mid-flight). */
	hasLanded: boolean;
	/** Claim the bee and fly it to this box. Attach to the card's focus/pointerdown. */
	claim: () => void;
}

export const useBizyCommentBoxFlight = ({ bonusId, enabled, cardRef, anchorRef }: UseBizyCommentBoxFlightParams): UseBizyCommentBoxFlightResult => {
	const activeBonusId = useCommentFlightOwner((state) => state.activeBonusId);
	const setActive = useCommentFlightOwner((state) => state.setActive);
	const registerAnchor = useBizyFlightStore((state) => state.registerAnchor);
	// The bee has "landed" once the flight to the comment box is parked (phase
	// flips from 'flying' to 'parked' on arrival). Used to hold back the resting
	// hint until the bee actually settles rather than the moment it's claimed.
	const isParkedOnCommentBox = useBizyFlightStore((state) => state.flight?.phase === 'parked' && state.flight?.destinationAnchorName === 'commentBox');

	const isOwner = activeBonusId === bonusId;
	const hasLanded = isOwner && isParkedOnCommentBox;

	// Send the bee home, but only if we still own it (guards races where another
	// box claimed ownership between the trigger firing and this running).
	const flyHome = useCallback(() => {
		cancelScheduledHome();
		if (useCommentFlightOwner.getState().activeBonusId !== bonusId) return;
		setActive(null);
		const store = useBizyFlightStore.getState();
		// Fly back to wherever Bizy came from (like the give box): to the chat panel
		// if she flew from there, otherwise off the right edge of the screen. Keeps
		// exactly one bee — the chat/nav bee resumes once the flight clears.
		if (!store.flyBackToSource()) store.clearFlight();
	}, [bonusId, setActive]);

	// Every fly-home trigger goes through this debounce. Switching to another
	// comment box tears down this composer (unmount) and/or clicks outside it —
	// both of which would otherwise send the bee home instantly, before the new
	// box mounts and claims. Deferring by a grace period lets that claim cancel
	// the pending home so the bee chains box-to-box instead of bouncing off the
	// nav. Crucially we do NOT clear ownership or start the home flight until the
	// timer fires, so the store still holds this box's parked flight for the new
	// box to chain from.
	const scheduleHome = useCallback(() => {
		cancelScheduledHome();
		scheduledHomeTimer = setTimeout(() => {
			scheduledHomeTimer = null;
			flyHome();
		}, FLY_HOME_GRACE_MS);
	}, [flyHome]);

	const claim = useCallback(() => {
		if (!enabled) return;
		// Engaging any comment box cancels a pending "go home" so the bee chains
		// box-to-box instead of bouncing back to the nav first.
		cancelScheduledHome();
		if (useCommentFlightOwner.getState().activeBonusId === bonusId) return;

		setActive(bonusId);

		// Only wait for the landing spot to settle when entering fresh from the nav.
		// Opening a below-the-fold comment box triggers a smooth scroll-into-view;
		// flying before it settles targets the pre-scroll position, so the bee lands
		// mid-box then snaps to the top. Waiting for two stable frames avoids that.
		//
		// When CHAINING box-to-box (a flight already exists) we must NOT wait: box B
		// registers its anchor while box A's flight is still parked, and the overlay's
		// parked-follow would instantly snap the bee to B. Flying on the first
		// measurable frame starts the A->B flight before that snap can win the race.
		const requireStableTarget = !useBizyFlightStore.getState().flight;

		let framesRemaining = MAX_FLIGHT_FRAMES;
		let lastTop: number | null = null;
		let stableSinceMs: number | null = null;
		const attempt = () => {
			if (useCommentFlightOwner.getState().activeBonusId !== bonusId) return;

			const now = performance.now();
			const top = anchorRef.current?.getBoundingClientRect().top ?? null;
			if (top === null) {
				stableSinceMs = null;
			} else if (lastTop === null || Math.abs(top - lastTop) >= 1) {
				// First measurement, or the anchor moved (e.g. mid scroll-into-view):
				// (re)start the stability window.
				stableSinceMs = now;
			}
			lastTop = top;

			const stableForMs = stableSinceMs === null ? 0 : now - stableSinceMs;
			const settled = !requireStableTarget || stableForMs >= TARGET_STABLE_MS || framesRemaining <= 1;
			if (settled && useBizyFlightStore.getState().flyToCommentBox()) return;
			if (--framesRemaining > 0) requestAnimationFrame(attempt);
		};
		requestAnimationFrame(attempt);
	}, [enabled, bonusId, setActive, anchorRef]);

	// Register this box's landing anchor only while we own the bee, so the flight
	// store's "first usable comment-box anchor" always resolves to the active box
	// (and the parked bee follows THIS box on scroll).
	useEffect(() => {
		if (!enabled || !isOwner) return;
		const element = anchorRef.current;
		if (!element) return;
		registerAnchor('commentBox', bonusId, element);
		return () => registerAnchor('commentBox', bonusId, null);
	}, [enabled, isOwner, bonusId, registerAnchor, anchorRef]);

	// Fly-home triggers, all funneling through the debounced scheduleHome so a
	// claim from another box can cancel them. Wired together in one effect with a
	// single cleanup:
	//  - pointerdown outside every comment zone. "Inside" = the white card or any
	//    element tagged COMMENT_ZONE_ATTR, plus any Chakra portal (`.chakra-portal`)
	//    since the toolbar's GIF/emoji/media pickers portal to <body> — treating
	//    them as inside keeps the bee parked while the user works those popovers.
	//    A click into a DIFFERENT comment box lands inside its zone, so we don't
	//    fly home — that box claims ownership instead.
	//  - Escape while focus is within the card.
	//  - the card scrolling out of the viewport.
	useEffect(() => {
		if (!enabled || !isOwner) return;

		const onPointerDown = (event: PointerEvent) => {
			const target = event.target as HTMLElement | null;
			if (target?.closest(`[${COMMENT_ZONE_ATTR}], .chakra-portal`)) return;
			scheduleHome();
		};
		const onKeyDown = (event: KeyboardEvent) => {
			if (event.key !== 'Escape') return;
			if (cardRef.current?.contains(document.activeElement)) scheduleHome();
		};
		document.addEventListener('pointerdown', onPointerDown, true);
		document.addEventListener('keydown', onKeyDown, true);

		// Scroll-away: only leave once the card has actually been visible and then
		// scrolls off. Ignoring the initial not-intersecting state avoids a false
		// home when a box opens below the fold (the feed's delayed scroll-into-view
		// brings it up a beat later); and coming back into view cancels a pending
		// home so a transient off-screen frame during that scroll doesn't dismiss
		// the bee right after it arrives.
		const card = cardRef.current;
		let hasBeenVisible = false;
		const observer =
			card && typeof IntersectionObserver !== 'undefined'
				? new IntersectionObserver(
						(entries) => {
							const entry = entries[entries.length - 1];
							if (entry.isIntersecting) {
								hasBeenVisible = true;
								cancelScheduledHome();
							} else if (hasBeenVisible) {
								scheduleHome();
							}
						},
						{ threshold: 0 }
					)
				: null;
		observer?.observe(card as Element);

		// NOTE: we intentionally do NOT cancelScheduledHome() here. React runs
		// effect cleanups in reverse order, so on unmount this would run AFTER the
		// unmount effect's scheduleHome() and wipe it, leaving the bee stuck. The
		// timer is owned by claim() (cancels) and flyHome() (cancels + runs).
		return () => {
			document.removeEventListener('pointerdown', onPointerDown, true);
			document.removeEventListener('keydown', onKeyDown, true);
			observer?.disconnect();
		};
	}, [enabled, isOwner, scheduleHome, cardRef]);

	// Fly home if the feature gate flips off (e.g. viewport shrinks to mobile).
	useEffect(() => {
		if (!enabled && useCommentFlightOwner.getState().activeBonusId === bonusId) flyHome();
	}, [enabled, bonusId, flyHome]);

	// Fly home on unmount (comment posted → composer closes, or thread collapsed,
	// or the user switched to another box which remounts a composer). Debounced
	// via scheduleHome so a claim from the box being opened cancels it and the
	// bee chains over instead of bouncing home.
	useEffect(() => {
		return () => {
			if (useCommentFlightOwner.getState().activeBonusId === bonusId) scheduleHome();
		};
	}, [bonusId, scheduleHome]);

	return { isOwner, hasLanded, claim };
};
